// morning.js — pure helpers for Morning Mode: local-date keying, the morning
// auto-route window, and the meds countdown derived from a STORED timestamp
// (never a client timer — the countdown must survive a force-close).
//
// No imports: safe to use anywhere, and trivially unit-testable.

// Local calendar date as "YYYY-MM-DD" (one morning_sessions row per local day).
export function todayLocalDate(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// "HH:MM" → minutes since local midnight, or null if unparseable.
export function parseHHMM(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = +m[1], min = +m[2];
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// Is `now` inside the configured morning window [start, end)?
export function isWithinMorningWindow(prefs, now = new Date()) {
  const start = parseHHMM(prefs?.morning_window_start) ?? 5 * 60;   // 05:00 default
  const end = parseHHMM(prefs?.morning_window_end) ?? 10 * 60;      // 10:00 default
  const cur = now.getHours() * 60 + now.getMinutes();
  return cur >= start && cur < end;
}

// Should opening the app drop straight into Morning Mode? Only when it's enabled,
// we're in the window, AND today's session is unstarted or not yet completed.
// A completed morning opens the normal home instead (spec guard).
export function shouldAutoRouteMorning({ prefs, session, now = new Date() }) {
  if (prefs?.morning_mode_enabled === false) return false;
  if (!isWithinMorningWindow(prefs, now)) return false;
  if (session && session.completed_at) return false;
  return true;
}

// Meds timer length in ms (default 60 min).
export function medsDurationMs(prefs) {
  const mins = Number.isFinite(+prefs?.meds_timer_minutes) ? +prefs.meds_timer_minutes : 60;
  return mins * 60 * 1000;
}

// When the meds timer fires, as a Date — or null if meds not given.
export function medsFireAt(session, prefs) {
  if (!session?.meds_given_at) return null;
  return new Date(new Date(session.meds_given_at).getTime() + medsDurationMs(prefs));
}

// Remaining ms on the meds timer (derived from meds_given_at, not a client clock
// we started). >0 while running, <=0 once elapsed, null when meds not given.
export function medsRemainingMs(session, prefs, now = new Date()) {
  const fire = medsFireAt(session, prefs);
  if (!fire) return null;
  return fire.getTime() - now.getTime();
}

export function medsIsRunning(session, prefs, now = new Date()) {
  const rem = medsRemainingMs(session, prefs, now);
  return rem != null && rem > 0;
}

// "M:SS" for a duration in ms (clamped at 0). Used by the roaming timer chip.
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Prep checklist keys stored in morning_sessions.prep jsonb.
export const PREP_STEPS = [
  // split_dose is only rendered when preferences.household.self_meds_label is
  // set (see lib/household.js). The label here is the neutral fallback used by
  // any consumer that renders the list without household context.
  { key: "split_dose", label: "Your medication" },
  { key: "water", label: "Fill water chamber" },
  { key: "filter", label: "Fresh filter in the bin" },
];
